import React, { useMemo, useState } from 'react';
/**
 * RulebookHintsPanel — Family-hints verkenner (P1-4).
 *
 * Doel: één plek waar een redacteur per family ziet wat de rulebooks
 * voorschrijven aan vraagtypes (familyTypeHints) én visuals
 * (familyVisualHints). Dit is de inverse van LiveRulebookCard, dat per
 * module toont. Hier kies je de family en ziet je alle matchende hints
 * vanuit elk relevant rulebook — een 1-blik check voor
 * "past mijn vraag bij deze family?".
 *
 * Gevoed door:
 *   - QUESTIONS_BY_FAMILY (alle bestaande families)
 *   - resolveFamilyTypeHints() / resolveFamilyVisualHints() (regelboeken)
 *   - getModuleRulebook() voor de module-context van de geselecteerde family
 *   - FAMILY_DISPLAY_NAMES voor mensvriendelijke labels
 */
import { TypeIcon, PaletteIcon, CheckCircle2Icon, XCircleIcon, SearchIcon, FilterIcon, InfoIcon } from 'lucide-react';
import { Input } from '../../../../components/ui/Input';
import { Select } from '../../../../components/ui/Select';
import { QUESTIONS_BY_FAMILY, FAMILY_DISPLAY_NAMES } from '../../../../content';
import { resolveFamilyTypeHints, resolveFamilyVisualHints, getModuleRulebook } from '../../../../governance';
import { ADMIN_MODULE_META } from '../../shared/moduleMetadata';
type FamilyRow = {
  familyId: string;
  displayName: string;
  shortCode: string;
  moduleName: string;
  questionCount: number;
  hasTypeHint: boolean;
  hasVisualHint: boolean;
};
export function RulebookHintsPanel() {
  const [search, setSearch] = useState('');
  const [moduleFilter, setModuleFilter] = useState<string>('all');
  const [hintFilter, setHintFilter] = useState<'all' | 'with-type' | 'with-visual' | 'without-any'>('all');
  const [selectedFamilyId, setSelectedFamilyId] = useState<string | null>(null);
  // Build the family rows once — we resolve hints upfront so we can
  // filter "with/without hint" without recomputing on each render.
  const rows = useMemo<FamilyRow[]>(() => {
    const out: FamilyRow[] = [];
    for (const [familyId, questions] of QUESTIONS_BY_FAMILY.entries()) {
      const shortCode = familyId.split('-')[0] || 'UNK';
      const meta = ADMIN_MODULE_META[shortCode];
      const typeHints = resolveFamilyTypeHints(familyId);
      const visualHints = resolveFamilyVisualHints(familyId);
      out.push({
        familyId,
        displayName: FAMILY_DISPLAY_NAMES[familyId] ?? familyId,
        shortCode,
        moduleName: meta?.name ?? shortCode,
        questionCount: questions.length,
        hasTypeHint: typeHints.length > 0,
        hasVisualHint: visualHints.length > 0
      });
    }
    return out.sort((a, b) => a.familyId.localeCompare(b.familyId));
  }, []);
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(r => {
      if (moduleFilter !== 'all' && r.shortCode !== moduleFilter) return false;
      if (hintFilter === 'with-type' && !r.hasTypeHint) return false;
      if (hintFilter === 'with-visual' && !r.hasVisualHint) return false;
      if (hintFilter === 'without-any' && (r.hasTypeHint || r.hasVisualHint)) return false;
      if (q !== '') {
        const hay = r.familyId.toLowerCase() + ' ' + r.displayName.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, search, moduleFilter, hintFilter]);
  // Auto-select first family on first render to avoid empty detail panel.
  const effectiveSelected = selectedFamilyId ?? filteredRows[0]?.familyId ?? null;
  const stats = useMemo(() => {
    const withType = rows.filter(r => r.hasTypeHint).length;
    const withVisual = rows.filter(r => r.hasVisualHint).length;
    const withAny = rows.filter(r => r.hasTypeHint || r.hasVisualHint).length;
    return {
      total: rows.length,
      withType,
      withVisual,
      withAny,
      coverage: rows.length === 0 ? 0 : Math.round(withAny / rows.length * 100)
    };
  }, [rows]);
  return <div className="space-y-5" data-id="element-4750" data-mp="27y177q6usgxl">
      {/* Header card */}
      <div className="rounded-lg border border-indigo-100 bg-indigo-50 p-4" data-id="element-4751" data-mp="3gw10teo7k961">
        <p className="text-sm text-indigo-900" data-id="element-4752" data-mp="3gydpsbsdvjsl">
          <strong data-id="element-4753" data-mp="3byhl0abv3obz">Family-hints verkenner</strong> — kies een family om te zien
          welke vraagtypes en visuals de regelboeken voorschrijven of afraden.
          Komt rechtstreeks uit{' '}
          <code className="font-mono bg-white/60 px-1 rounded" data-id="element-4754" data-mp="3kx30txpy8p92">
            familyTypeHints
          </code>{' '}
          en{' '}
          <code className="font-mono bg-white/60 px-1 rounded" data-id="element-4755" data-mp="3o6enwr7s57x">
            familyVisualHints
          </code>{' '}
          in{' '}
          <code className="font-mono bg-white/60 px-1 rounded" data-id="element-4756" data-mp="ci2wx0h9cxi2">
            src/governance/rulebooks/
          </code>
          .
        </p>
      </div>

      {/* Coverage tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-4757" data-mp="8j3kkut75ygl">
        <CoverageTile label="Families totaal" value={stats.total} accent="slate" data-id="element-4758" data-mp="mh0h5r7ni4v9" />
        <CoverageTile label="Met type-hint" value={stats.withType} accent="purple" data-id="element-4759" data-mp="hfb4k74s5j52" />
        <CoverageTile label="Met visual-hint" value={stats.withVisual} accent="emerald" data-id="element-4760" data-mp="162adrjluyllv" />
        <CoverageTile label="Dekking" value={`${stats.coverage}%`} accent="indigo" data-id="element-4761" data-mp="r5t3kvpend0w" />
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row" data-id="element-4762" data-mp="w5xnkvpiupvv">
        <div className="flex-1" data-id="element-4763" data-mp="ibxytos73ols">
          <Input type="text" placeholder="Zoek op family ID of naam..." value={search} onChange={e => {
          setSearch(e.target.value);
          setSelectedFamilyId(null);
        }} icon={<SearchIcon className="w-4 h-4" data-id="element-4765" data-mp="deoe2mfr414a" />} variant="admin" size="sm" data-id="element-4764" data-mp="iamwlteeeows" />
        </div>
        <Select value={moduleFilter} onChange={e => {
        setModuleFilter(e.target.value);
        setSelectedFamilyId(null);
      }} options={[{
        value: 'all',
        label: 'Alle vakken'
      }, ...Object.entries(ADMIN_MODULE_META).map(([code, meta]) => ({
        value: code,
        label: `${meta.name} (${code})`
      }))]} variant="admin" size="sm" fullWidth={false} data-id="element-4766" data-mp="3vjrlidt6wp7v" />
        <Select value={hintFilter} onChange={e => {
        setHintFilter(e.target.value as typeof hintFilter);
        setSelectedFamilyId(null);
      }} options={[{
        value: 'all',
        label: 'Alle families'
      }, {
        value: 'with-type',
        label: 'Met type-hint'
      }, {
        value: 'with-visual',
        label: 'Met visual-hint'
      }, {
        value: 'without-any',
        label: 'Zonder hint'
      }]} variant="admin" size="sm" fullWidth={false} data-id="element-4767" data-mp="2p0q96ua0ggxh" />
      </div>

      <p className="text-sm text-gray-500 flex items-center gap-2" data-id="element-4768" data-mp="3fa7hsuj3ilxg">
        <FilterIcon className="h-3.5 w-3.5" data-id="element-4769" data-mp="3ka28oy2vfbii" />
        {filteredRows.length} van {rows.length} families
      </p>

      {/* Two-pane layout: family list + detail */}
      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4" data-id="element-4770" data-mp="3p23f43dcy80m">
        {/* Family list */}
        <div className="rounded-xl border border-gray-100 bg-white overflow-hidden max-h-[640px] overflow-y-auto" data-id="element-4771" data-mp="3tzlrk3ql35dd">
          {filteredRows.length === 0 ? <div className="py-12 text-center text-sm text-gray-500" data-id="element-4772" data-mp="6uxhnryrnqsb">
              <SearchIcon className="mx-auto h-7 w-7 text-gray-300 mb-2" data-id="element-4773" data-mp="bua2k762uns9" />
              Geen families gevonden.
            </div> : <ul role="list" data-id="element-4774" data-mp="28nu2g6iha7pm">
              {filteredRows.map(row => {
            const isSelected = row.familyId === effectiveSelected;
            return <li key={row.familyId} data-id="element-4775" data-mp="jd9ks9hsy9a1">
                    <button onClick={() => setSelectedFamilyId(row.familyId)} className={`w-full text-left px-3 py-2.5 border-b border-gray-50 transition-colors ${isSelected ? 'bg-indigo-50 border-l-2 border-l-indigo-500' : 'hover:bg-gray-50 border-l-2 border-l-transparent'}`} data-id="element-4776" data-mp="jc87dydbdap5">
                      <div className="flex items-center justify-between gap-2" data-id="element-4777" data-mp="eniwsegoe52">
                        <code className={`text-xs font-mono font-semibold truncate ${isSelected ? 'text-indigo-700' : 'text-gray-800'}`} data-id="element-4778" data-mp="gqia31i7jmg">
                          {row.familyId}
                        </code>
                        <div className="flex gap-1 flex-shrink-0" data-id="element-4779" data-mp="5d8iqqpswvsz">
                          {row.hasTypeHint && <span title="Heeft type-hint" className="inline-block w-1.5 h-1.5 rounded-full bg-purple-500" data-id="element-4780" data-mp="1lqfb4yhv5rx1" />}
                          {row.hasVisualHint && <span title="Heeft visual-hint" className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-4781" data-mp="33rg5071ekh18" />}
                        </div>
                      </div>
                      <p className={`text-xs mt-0.5 truncate ${isSelected ? 'text-indigo-600' : 'text-gray-500'}`} data-id="element-4782" data-mp="3ifayvqr5crfw">
                        {row.displayName}
                      </p>
                      <p className="text-[10px] text-gray-400 mt-0.5" data-id="element-4783" data-mp="3do2s72h7bk6f">
                        {row.shortCode} · {row.questionCount} vragen
                      </p>
                    </button>
                  </li>;
          })}
            </ul>}
        </div>

        {/* Detail panel */}
        <div className="min-h-[400px]" data-id="element-4784" data-mp="32lvs6v612sqw">
          {effectiveSelected ? <FamilyHintsDetail familyId={effectiveSelected} data-id="element-4785" data-mp="32jt44gofr9ot" /> : <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 py-16 text-center" data-id="element-4786" data-mp="37jxj229p3wcx">
              <InfoIcon className="mx-auto h-7 w-7 text-gray-300 mb-2" data-id="element-4787" data-mp="2tn0hp6vyawhs" />
              <p className="text-sm text-gray-500" data-id="element-4788" data-mp="2xl83ig59unzu">
                Selecteer een family om de hints te zien.
              </p>
            </div>}
        </div>
      </div>
    </div>;
}
// ─── Detail panel ───────────────────────────────────────────────────────────
function FamilyHintsDetail({
  familyId
}: {
  familyId: string;
}) {
  const typeHints = resolveFamilyTypeHints(familyId);
  const visualHints = resolveFamilyVisualHints(familyId);
  const shortCode = familyId.split('-')[0] || '';
  const moduleMeta = ADMIN_MODULE_META[shortCode];
  // Find parent rulebook for module-level preferred types/visuals as fallback.
  const parentModuleId = typeHints[0]?.moduleId ?? visualHints[0]?.moduleId;
  const parentRulebook = parentModuleId ? getModuleRulebook(parentModuleId) : null;
  return <div className="rounded-xl border border-gray-100 bg-white overflow-hidden" data-id="element-4789" data-mp="1hc1iiwpdy7ng">
      {/* Family header */}
      <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-br from-gray-50 to-white" data-id="element-4790" data-mp="1vbkqnpi75izr">
        <div className="flex items-center gap-2 mb-1" data-id="element-4791" data-mp="1r7d15q3wzz0h">
          <code className="text-base font-mono font-bold text-gray-900" data-id="element-4792" data-mp="257nowk9xgxhf">
            {familyId}
          </code>
          {moduleMeta && <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold text-white ${moduleMeta.color}`} data-id="element-4793" data-mp="2092rstpz2tx7">
              {shortCode}
            </span>}
        </div>
        <p className="text-sm text-gray-600" data-id="element-4794" data-mp="2a1i6ni5z32dv">
          {FAMILY_DISPLAY_NAMES[familyId] ?? '—'}
        </p>
        {parentRulebook && <p className="text-xs text-gray-500 mt-1.5" data-id="element-4795" data-mp="2j0lqiocyrltf">
            Rulebook: <strong data-id="element-4796" data-mp="2ixzk4s1t7ccs">{parentRulebook.displayName}</strong> ·{' '}
            {parentRulebook.didactics.coreSummary}
          </p>}
      </div>

      <div className="p-5 space-y-5" data-id="element-4797" data-mp="1lmkaveplm2xa">
        {/* Type hints */}
        <Section icon={<TypeIcon className="h-4 w-4 text-purple-600" data-id="element-4799" data-mp="1pmujkz4rz7fu" />} title="Vraagtype-hints" count={typeHints.length} data-id="element-4798" data-mp="1pncki92du9u3">
          {typeHints.length === 0 ? <EmptyHint kind="vraagtype" data-id="element-4800" data-mp="1zd5u54iysevp" /> : <ul className="space-y-3" data-id="element-4801" data-mp="1zcntzwt6lgii">
              {typeHints.map((hint, i) => <li key={`${hint.moduleId}-${i}`} className="rounded-lg border border-purple-100 bg-purple-50/40 p-3" data-id="element-4802" data-mp="24afkcme6gukl" data-mpi={i}>
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap" data-id="element-4803" data-mp="2dcygyordeaye">
                    <code className="font-mono text-[10px] bg-white px-1.5 py-0.5 rounded border border-purple-100 text-purple-700" data-id="element-4804" data-mp="exc249nzotjw">
                      {hint.familyIdPattern}
                    </code>
                    <span className="text-[10px] text-gray-500 uppercase tracking-wide" data-id="element-4805" data-mp="bfod0muckbf2">
                      {hint.moduleId}
                    </span>
                  </div>
                  {hint.preferredTypes && hint.preferredTypes.length > 0 && <ChipRow label="Voorkeur" labelIcon={<CheckCircle2Icon className="h-3 w-3 text-emerald-600" data-id="element-4807" data-mp="3tr1dqqtvqx51" />} chips={hint.preferredTypes} chipClass="bg-emerald-50 text-emerald-700 border-emerald-100" data-id="element-4806" data-mp="1n8u9nb87it4" />}
                  {hint.discouragedTypes && hint.discouragedTypes.length > 0 && <ChipRow label="Afgeraden" labelIcon={<XCircleIcon className="h-3 w-3 text-red-500" data-id="element-4809" data-mp="361gsg1a97nsl" />} chips={hint.discouragedTypes} chipClass="bg-red-50 text-red-700 border-red-100 line-through decoration-red-300" data-id="element-4808" data-mp="gdy86xg2nc0o" />}
                  <p className="text-xs text-gray-600 mt-1.5 leading-relaxed" data-id="element-4810" data-mp="xok23pbc1ife">
                    {hint.note}
                  </p>
                </li>)}
            </ul>}
        </Section>

        {/* Visual hints */}
        <Section icon={<PaletteIcon className="h-4 w-4 text-emerald-600" data-id="element-4812" data-mp="1bnjo0p7gnj7v" />} title="Visual-hints" count={visualHints.length} data-id="element-4811" data-mp="1blqe911jwivn">
          {visualHints.length === 0 ? <EmptyHint kind="visual" data-id="element-4813" data-mp="12md8ojgjseg7" /> : <ul className="space-y-3" data-id="element-4814" data-mp="12nwl1yz325cp">
              {visualHints.map((hint, i) => <li key={`${hint.moduleId}-${i}`} className="rounded-lg border border-emerald-100 bg-emerald-50/40 p-3" data-id="element-4815" data-mp="17mrfxzabrkd6" data-mpi={i}>
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap" data-id="element-4816" data-mp="3sbnm35sie6l3">
                    <code className="font-mono text-[10px] bg-white px-1.5 py-0.5 rounded border border-emerald-100 text-emerald-700" data-id="element-4817" data-mp="3jt3nmpvqxa3g">
                      {hint.familyIdPattern}
                    </code>
                    <span className="text-[10px] text-gray-500 uppercase tracking-wide" data-id="element-4818" data-mp="3tr00pn88uf47">
                      {hint.moduleId}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-1.5" data-id="element-4819" data-mp="312ufz0h6wq4d">
                    {hint.preferredStimulus && <span className="inline-flex items-center gap-1 text-xs bg-white border border-emerald-200 text-emerald-800 px-2 py-0.5 rounded" data-id="element-4820" data-mp="2w5c3x17vlus5">
                        <span className="text-[9px] uppercase tracking-wider text-emerald-600" data-id="element-4821" data-mp="3av07k93j55j1">
                          Stimulus
                        </span>
                        <code className="font-mono text-[11px]" data-id="element-4822" data-mp="2ibbcksj97a1t">
                          {hint.preferredStimulus}
                        </code>
                      </span>}
                    {hint.preferredSupport && <span className="inline-flex items-center gap-1 text-xs bg-white border border-emerald-200 text-emerald-800 px-2 py-0.5 rounded" data-id="element-4823" data-mp="m9rg33mdeeq6">
                        <span className="text-[9px] uppercase tracking-wider text-emerald-600" data-id="element-4824" data-mp="r0zrg579ga5z">
                          Support
                        </span>
                        <code className="font-mono text-[11px]" data-id="element-4825" data-mp="3uhq0yu21x5cf">
                          {hint.preferredSupport}
                        </code>
                      </span>}
                  </div>
                  {hint.questionTypeExempt && hint.questionTypeExempt.length > 0 && <p className="text-[11px] text-gray-500 italic mb-1" data-id="element-4826" data-mp="ccd1l9k0c00i">
                        Uitgezonderd voor:{' '}
                        {hint.questionTypeExempt.map((t, j) => <code key={j} className="font-mono bg-gray-100 px-1 rounded mr-1" data-id="element-4827" data-mp="1nppa3xqpp6t0" data-mpi={j}>
                            {t}
                          </code>)}
                      </p>}
                  <p className="text-xs text-gray-600 leading-relaxed" data-id="element-4828" data-mp="1o3jcb2g1c7gr">
                    {hint.note}
                  </p>
                </li>)}
            </ul>}
        </Section>

        {/* Module-level fallback */}
        {parentRulebook && <div className="rounded-lg border border-gray-100 bg-gray-50 p-3" data-id="element-4829" data-mp="1a4tflnx4o9sx">
            <p className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold mb-2 flex items-center gap-1.5" data-id="element-4830" data-mp="155qa62d8k1kf">
              <InfoIcon className="h-3 w-3" data-id="element-4831" data-mp="1156b2t93qnvn" />
              Module-fallback ({parentRulebook.displayName})
            </p>
            <div className="space-y-2 text-xs" data-id="element-4832" data-mp="2zjhgarwcw3b4">
              {parentRulebook.questionTypes.preferredTypes.length > 0 && <div className="flex flex-wrap gap-1.5 items-center" data-id="element-4833" data-mp="2vgbgo33r1adm">
                  <span className="text-gray-500" data-id="element-4834" data-mp="3erpdkre4dd50">Voorkeur vraagtypes:</span>
                  {parentRulebook.questionTypes.preferredTypes.map(t => <code key={t} className="font-mono bg-white border border-gray-200 text-gray-700 px-1.5 py-0.5 rounded text-[10px]" data-id="element-4835" data-mp="351m8hstq48qb">
                      {t}
                    </code>)}
                </div>}
              {parentRulebook.visuals.preferredAidTypes && parentRulebook.visuals.preferredAidTypes.length > 0 && <div className="flex flex-wrap gap-1.5 items-center" data-id="element-4836" data-mp="2m4308rf3au4y">
                    <span className="text-gray-500" data-id="element-4837" data-mp="2r0tgpfut3rqw">Voorkeur visuals:</span>
                    {parentRulebook.visuals.preferredAidTypes.map(v => <code key={v} className="font-mono bg-white border border-gray-200 text-gray-700 px-1.5 py-0.5 rounded text-[10px]" data-id="element-4838" data-mp="k9ar5ukzkxm9">
                        {v}
                      </code>)}
                  </div>}
            </div>
          </div>}
      </div>
    </div>;
}
// ─── Small UI primitives ────────────────────────────────────────────────────
function Section({
  icon,
  title,
  count,
  children
}: {
  icon: React.ReactNode;
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  return <div data-id="element-4839" data-mp="1d638l8hapbz5">
      <div className="flex items-center gap-2 mb-2" data-id="element-4840" data-mp="1d4iaj8vrvawj">
        {icon}
        <h4 className="text-sm font-semibold text-gray-800" data-id="element-4841" data-mp="1r52u479ihar3">{title}</h4>
        <span className="text-xs text-gray-400 font-medium" data-id="element-4842" data-mp="1r38i5ig7wsz5">({count})</span>
      </div>
      {children}
    </div>;
}
function ChipRow({
  label,
  labelIcon,
  chips,
  chipClass
}: {
  label: string;
  labelIcon: React.ReactNode;
  chips: string[];
  chipClass: string;
}) {
  return <div className="flex flex-wrap items-center gap-1.5 mb-1" data-id="element-4843" data-mp="3owxc5v03kxho">
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-gray-500 font-semibold" data-id="element-4844" data-mp="2w8du7zja9g8m">
        {labelIcon}
        {label}
      </span>
      {chips.map(c => <code key={c} className={`font-mono text-[11px] px-1.5 py-0.5 rounded border ${chipClass}`} data-id="element-4845" data-mp="35z8jlcrtsvya">
          {c}
        </code>)}
    </div>;
}
function EmptyHint({
  kind
}: {
  kind: 'vraagtype' | 'visual';
}) {
  return <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50/50 p-4 text-center" data-id="element-4846" data-mp="2dc4kfofezbuf">
      <p className="text-xs text-gray-500" data-id="element-4847" data-mp="28djklq42qu6i">
        Geen {kind}-hint gedefinieerd voor deze family.
      </p>
      <p className="text-[11px] text-gray-400 mt-1" data-id="element-4848" data-mp="2i9e74wbf59ek">
        De module-fallback hieronder geldt.
      </p>
    </div>;
}
function CoverageTile({
  label,
  value,
  accent
}: {
  label: string;
  value: number | string;
  accent: 'slate' | 'purple' | 'emerald' | 'indigo';
}) {
  const map = {
    slate: 'bg-slate-50 border-slate-100 text-slate-700',
    purple: 'bg-purple-50 border-purple-100 text-purple-700',
    emerald: 'bg-emerald-50 border-emerald-100 text-emerald-700',
    indigo: 'bg-indigo-50 border-indigo-100 text-indigo-700'
  } as const;
  return <div className={`rounded-lg border p-3 text-center ${map[accent]}`} data-id="element-4849" data-mp="3rbpkkxoexq51">
      <p className="text-2xl font-bold" data-id="element-4850" data-mp="4mrg0p0l9s6">{value}</p>
      <p className="text-xs font-medium opacity-80" data-id="element-4851" data-mp="5emnaep95hx">{label}</p>
    </div>;
}